import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useAuth } from '../../features/auth/useAuth';
import { usePermissions } from '../../features/auth/usePermissions';
import { useKnowledgeHealth } from '../../features/learning/hooks';
import { NotificationDot } from '../../features/design-system/components';
import { useBarObrashcheniy } from '../../features/obrashcheniya/hooks';
import { adresOcheredi } from '../../features/obrashcheniya/shapkaAdresa';
import { useFilialShapki } from '../../features/multi-tenant/hooks';
import { usePhoneAlerts } from '../../features/telephony/phoneAlerts';
import { useNastroykiUvedomleniy } from '../../features/uvedomleniya/nastroyki';
import { useVypadashka } from './useVypadashka';

/**
 * 9. Колокольчик — ОСТАЛЬНЫЕ уведомления (header1, макет Юрия 04.10.2026).
 *
 * Три очереди переписок стоят в строке своими кнопками и сюда не дублируются
 * (§31.8). Здесь — то, что из строки не видно: ошибка подключения телефона
 * (объект, причина, действие), «Пора перезвонить» по перепискам и карточки на
 * столе. Каждый пункт ведёт к конкретному месту; пустого «0» без источника нет.
 */
export function ShapkaKolokol() {
  const { can } = usePermissions();
  const filial = useFilialShapki();
  const canLearn = can('ai_memory.learn');
  const { data: health } = useKnowledgeHealth(canLearn);
  const mozhnoPerepiski = can('inquiry.queue.view');
  const { data: bar } = useBarObrashcheniy(mozhnoPerepiski);
  const phone = usePhoneAlerts();
  // `s720`: всплытие и звук входящих отключаемы — настройка этого человека в этом браузере.
  const { user } = useAuth();
  const [uved, setUved] = useNastroykiUvedomleniy(mozhnoPerepiski && user ? user.id : null);
  const { open: menuOpen, setOpen: setMenuOpen, ref: menuRef } = useVypadashka<HTMLDivElement>();

  // Слова приезжают с баром; без права на переписки — запасное слово того же кода.
  const t = (kod: string, zapasnoe: string) => bar?.slova?.[kod] ?? zapasnoe;
  const pending = canLearn ? health?.pending ?? 0 : 0;
  const pora = mozhnoPerepiski ? bar?.pora_perezvonit ?? bar?.uvedomleniya?.find((u) => u.vid === 'pora_perezvonit')?.count ?? 0 : 0;
  const vsego = phone.errors.length + (pora > 0 ? 1 : 0) + (pending > 0 ? 1 : 0);
  const zagolovok = t('messenger.header.bell', 'Уведомления');

  return (
    <div className="hdr-item hdr-menu-wrap" data-hdr="bell" ref={menuRef}>
      <button type="button" className="hdr-btn hdr-icon" aria-label={zagolovok} title={zagolovok} aria-expanded={menuOpen} onClick={() => setMenuOpen(!menuOpen)}>
        <NotificationDot show={vsego > 0} count={vsego} tone="danger">
          <Bell size={20} />
        </NotificationDot>
      </button>
      {menuOpen && (
        <div className="hdr-menu hdr-menu-right" role="menu">
          {phone.errors.map((oshibka) => (
            <div key={oshibka} className="hdr-menu-alert" role="alert">
              <strong>{t('messenger.header.phone', 'Телефон')}</strong>
              <span>{oshibka}</span>
              {phone.reconnect !== null && (
                <button type="button" onClick={() => { setMenuOpen(false); phone.reconnect?.(); }}>
                  {t('messenger.header.reconnect', 'Подключить снова')}
                </button>
              )}
            </div>
          ))}
          {pora > 0 && (
            <Link role="menuitem" to={adresOcheredi(filial, 'pora_perezvonit')} data-vid="pora_perezvonit">
              <span>{t('messenger.filters.callback', 'Пора перезвонить')}</span><b>{pora}</b>
            </Link>
          )}
          {pending > 0 && (
            <Link role="menuitem" to="/learning/staged">
              <span>{t('messenger.header.kb_cards', 'Карточки на столе')}</span><b>{pending}</b>
            </Link>
          )}
          {vsego === 0 && <p className="hdr-menu-empty">{t('messenger.header.bell_empty', 'Новых уведомлений нет')}</p>}
          {mozhnoPerepiski && (
            <div className="hdr-menu-nastroyki" data-testid="uved-nastroyki">
              <strong>{t('uvedomlenie.nastroyki', 'Уведомления о входящих')}</strong>
              <label>
                <input type="checkbox" checked={uved.vsplytie} onChange={(e) => setUved({ vsplytie: e.target.checked })} data-testid="uved-vsplytie" />
                <span>{t('uvedomlenie.vsplytie', 'Всплывающие уведомления')}</span>
              </label>
              <label>
                <input type="checkbox" checked={uved.zvuk} onChange={(e) => setUved({ zvuk: e.target.checked })} data-testid="uved-zvuk" />
                <span>{t('uvedomlenie.zvuk', 'Звук')}</span>
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
